import { produce } from 'immer'
import lodash from 'lodash'
import * as R from 'ramda'
import React, { useCallback, useImperativeHandle, useLayoutEffect, useMemo, useState } from 'react'

import { KitSize } from 'components/uiKit/KitTypes'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { EMPTY_OBJECT, IDENTITY } from 'constants/commonConstans'

import { UseFormConfirmExit } from '../components'
import { DEFAULT_FORM_ERROR_MESSAGES } from '../constants'
import { isCommonError, isInputError } from '../typeguards'
import type {
  FormConfig,
  IFieldChange,
  IFieldConfig,
  IFieldMeta,
  IFormContext,
  IFormProps,
} from '../types'
import { validateRules } from '../validateRules'
import FormContext from './FormContext'

export type FormHandle<Values extends object = object> = IFormContext<Values>

const FormInner = <Values extends object, Result>(
  {
    defaultValues = EMPTY_OBJECT,
    inheritValues = EMPTY_OBJECT,
    initialValues = EMPTY_OBJECT,
    layout = 'vertical',
    size = KitSize.M,
    customErrorMessages = EMPTY_OBJECT,
    disabled = false,
    blockOnExit = false,
    onSubmit,
    onChangeValues,
    onFieldChange,
    onReset,
    onFieldBlur,
    onFieldFocus,
    children,
  }: IFormProps<Values, Result>,
  ref: React.Ref<FormHandle<Values>>,
) => {
  const [metas, setMetas] = useState<Record<string, IFieldMeta>>(EMPTY_OBJECT)
  const [values, setValues] = useState(initialValues)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [formConfig, setFormConfig] = useState<FormConfig>({})
  const isDirty = useMemo(() => Object.values(metas).some((meta) => meta.dirty), [metas])
  const isTouched = useMemo(() => Object.values(metas).some((meta) => meta.touched), [metas])
  const isValid = useMemo(
    () => !Object.values(metas).some((meta) => meta.error || meta.commonError),
    [metas],
  )
  const mergedErrorMessages = useMemo(
    () => ({ ...DEFAULT_FORM_ERROR_MESSAGES, ...customErrorMessages }),
    [customErrorMessages],
  )

  const onChangeMeta = useCallback((name: string, meta: IFieldMeta) => {
    setMetas(
      produce((draft) => {
        if (!draft[name]) {
          draft[name] = meta
        } else {
          draft[name].touched = meta.touched ?? draft[name].touched
          draft[name].dirty = meta.dirty ?? draft[name].dirty
          draft[name].error = meta.error ?? draft[name].error
        }
      }),
    )
  }, [])

  const onChangeValue = useCallback(
    (fieldChanges: IFieldChange | IFieldChange[]) => {
      if (Array.isArray(fieldChanges)) {
        const isError = fieldChanges.some((change) => change.meta?.error)
        if (!isError) {
          onFieldChange?.(fieldChanges)
        }

        const newValues = fieldChanges.map((changes) => {
          const { value, name } = changes
          return (prevValues: Values) =>
            name
              ? produce(prevValues, (draft) => lodash.set(draft, name, value))
              : (value as Values)
        }) as [(prevValues: Values) => Values]

        const newMeta = fieldChanges.map((changes) => {
          const { name, meta } = changes
          if (!name || !meta) {
            return IDENTITY
          }
          return (prevValues: Values) =>
            produce(prevValues, (prevMeta: Record<string, IFieldMeta>) => {
              for (const field in prevMeta) {
                // Reset common error for all fields
                prevMeta[field].commonError = false
              }
              if (!prevMeta[name]) {
                prevMeta[name] = meta
              } else {
                prevMeta[name].touched = meta.touched ?? prevMeta[name].touched
                prevMeta[name].dirty = meta.dirty ?? prevMeta[name].dirty
                prevMeta[name].error = meta.error ?? prevMeta[name].error
              }
            })
        }) as [(prevMeta: Record<string, IFieldMeta>) => Record<string, IFieldMeta>]

        setValues((prevValues) => {
          const values = R.compose(...newValues)(prevValues)
          onChangeValues?.(values)
          return values
        })

        setMetas(R.compose(...newMeta))
      } else {
        const { value, name, meta } = fieldChanges
        if (!meta?.error) {
          onFieldChange?.({ name, value, meta })
        }

        setValues((prevValues) => {
          const newValues = name
            ? produce(prevValues, (draft) => lodash.set(draft, name, value))
            : (value as Values)
          onChangeValues?.(newValues)
          return newValues
        })

        if (name && meta) {
          setMetas(
            produce((draft) => {
              for (const field in draft) {
                // Reset common error for all fields
                draft[field].commonError = false
              }
              if (!draft[name]) {
                draft[name] = meta
              } else {
                draft[name].touched = meta.touched ?? draft[name].touched
                draft[name].dirty = meta.dirty ?? draft[name].dirty
                draft[name].error = meta.error ?? draft[name].error
              }
            }),
          )
        }
      }
    },
    [onChangeValues, onFieldChange],
  )

  const registerFormField = useCallback((name: string, config: IFieldConfig) => {
    setFormConfig(
      produce((draft) => {
        draft[name] = config
      }),
    )
  }, [])

  const unregisterFormField = useCallback((name: string) => {
    setFormConfig(
      produce((draft) => {
        delete draft[name]
      }),
    )
  }, [])

  const validateForm = useCallback((): boolean => {
    let valid = true

    for (const name in formConfig) {
      const config = formConfig[name]
      const value = lodash.get(values, name)
      const error = config.rules ? validateRules(value, config.rules) : false

      if (error) {
        console.info(`Validation error in field "${name}": ${error}`)
        valid = false
      }

      setMetas(
        produce((draft) => {
          if (!draft[name]) {
            draft[name] = {}
          }
          draft[name].error = error
          draft[name].touched = true
        }),
      )
    }

    return valid
  }, [formConfig, values])

  const submit = useCallback(async () => {
    if (!isValid || !onSubmit) {
      console.warn(`skip submit: ${!isValid ? 'form is invalid' : 'onSubmit is not defined'}`)
      return
    }

    if (!validateForm()) {
      console.warn(`skip submit: validateForm failed`)
      return
    }

    try {
      setSubmitting(true)
      const res = await onSubmit(values)
      const errors = (res && res.errors) || []

      if (errors.length) {
        errors.forEach((error) => {
          const errorReason = error?.reason || ''
          const dictionaryError = mergedErrorMessages[errorReason]
          const textError =
            (typeof dictionaryError === 'function'
              ? dictionaryError(error?.value)
              : dictionaryError) || errorReason

          if (isInputError(error)) {
            setMetas(
              produce((draft) => {
                if (error.field && draft[error.field]) {
                  draft[error.field].error = textError
                  draft[error.field].touched = true
                } else if (error.field) {
                  draft[error.field] = { error: textError, touched: true }
                }
              }),
            )
          }

          if (isCommonError(error)) {
            error.fields?.forEach((field) => {
              setMetas(
                produce((draft) => {
                  if (draft[field]) {
                    draft[field].commonError = true
                  } else {
                    draft[field] = { commonError: true }
                  }
                }),
              )
            })
            notify({ message: textError || error.reason || '', type: NotificationType.error })
          }
        })
        return
      }
      return res
    } catch (error) {
      console.error('common.formError', error)
      return
    } finally {
      setSubmitting(false)
    }
  }, [onSubmit, mergedErrorMessages, values, isValid, validateForm])

  const resetFields = useCallback(() => {
    onReset?.()
    setValues(initialValues)
    onChangeValues?.(initialValues)
    setMetas(EMPTY_OBJECT)
  }, [onReset, setMetas, onChangeValues, initialValues])

  // TODO: use useEffect?
  // For now use useLayoutEffect, because synchronization of value for fields
  // (There was an error with very fast duplication of array complex elements (Tabs, Accordion, etc.)
  useLayoutEffect(() => {
    if (initialValues) {
      setValues(initialValues)
    }
  }, [initialValues])

  const context = useMemo(
    () => ({
      metas,
      defaultValues,
      inheritValues,
      initialValues,
      formConfig,
      values,
      registerFormField,
      unregisterFormField,
      setMetas,
      setValues,
      resetFields,
      submit,
      onChangeValue,
      onChangeMeta,
      onFieldBlur,
      onFieldFocus,
      layout,
      size,
      submitting,
      disabled,
      isDirty,
      isTouched,
      isValid,
    }),
    [
      defaultValues,
      inheritValues,
      initialValues,
      values,
      metas,
      formConfig,
      resetFields,
      submit,
      onChangeValue,
      onChangeMeta,
      onFieldBlur,
      onFieldFocus,
      registerFormField,
      unregisterFormField,
      layout,
      size,
      submitting,
      disabled,
      isDirty,
      isTouched,
      isValid,
    ],
  )

  useImperativeHandle(ref, () => context as FormHandle<Values>, [context])

  return (
    <FormContext.Provider value={context as IFormContext<object>}>
      {blockOnExit && <UseFormConfirmExit />}
      {children}
    </FormContext.Provider>
  )
}

const Form = React.forwardRef(FormInner) as <Values extends object, Result>(
  props: IFormProps<Values, Result> & { ref?: React.Ref<FormHandle<Values>> },
) => React.ReactElement

export default React.memo(Form) as typeof Form
